import React, { useEffect, useRef } from "react";

import { keyframes } from "@emotion/react";
import { css } from "@emotion/react";
import { Flex, Card, Button, Box, ThemeUIStyleObject, Text } from "theme-ui";

import { BaseProp } from "../@types/base-types";
import { useTheme } from "../theme";
import { Icon } from "./Icon";
import "./dialog-animation.css";

type DialogIntent = "success" | "warning" | "danger" | "info";

type DialogProps = {
  dialogSx?: ThemeUIStyleObject;
  intent?: DialogIntent;
  title: string;
  icon?: React.ReactNode;
  cancelAriaLabel?: string;
  onCancel: () => void;
} & BaseProp;

const iconFromIntent = (intent: DialogIntent | undefined) => {
  switch (intent) {
    case "success":
      return <Icon name="check-circle" color="success" aria-label="Success" />;
    case "warning":
      return <Icon name="exclamation-triangle" color="warning" aria-label="Warning" />;
    case "danger":
      return <Icon name="exclamation-triangle" color="danger" aria-label="Danger" />;
    case "info":
      return <Icon name="info-circle" color="info" aria-label="Info" />;
  }
  return null;
};

const scaleInOutAnimation = keyframes(css`
  from {
    transform: scale(0);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`);

const scaleOutAnimation = keyframes(css`
  from {
    transform: scale(1);
    opacity: 1;
  }
  to {
    transform: scale(0);
    opacity: 0;
  }
`);

export const Dialog: React.FC<DialogProps> = ({
  dialogSx,
  intent,
  title,
  icon,
  cancelAriaLabel,
  onCancel,
  children
}) => {
  const dialogRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();

  return (
    <Card
      ref={dialogRef}
      variant="cards.dialog"
      sx={{
        ...dialogSx,
        animation: `${scaleInOutAnimation} 0.3s ease-in-out`,
        "&.closing": {
          animation: `${scaleOutAnimation} 0.3s ease-in-out` // Apply the close animation to the Dialog
        }
      }}
    >
      {intent ? <Box sx={{ height: "4px", bg: intent, borderRadius: "3px 3px 0 0" }} /> : null}
      <Flex
        sx={{
          width: "100%",
          flexDirection: "column",
          alignItems: "center",
          gap: "8px"
        }}
      >
        <Flex
          sx={{
            width: "100%",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "row"
          }}
        >
          <Box sx={{ width: "36px" }}></Box>
          <Text variant="text.msTitle24" sx={{ color: "B100", flex: 1, textAlign: "center" }}>
            {icon || iconFromIntent(intent)}
            {title}
          </Text>
          <Flex sx={{ width: "36px", flexDirection: "row", justifyContent: "flex-end" }}>
            <Button
              variant="icon"
              onClick={() => {
                dialogRef?.current?.classList?.add?.("closing");
                setTimeout(() => {
                  onCancel();
                  dialogRef?.current?.classList?.remove?.("closing");
                }, 300); // Ensure this delay matches the animation duration
              }}
              sx={{}}
            >
              <Icon
                name="times"
                size="xl"
                aria-label={cancelAriaLabel || "Cancel"}
                color={theme.colors.B100}
              />
            </Button>
          </Flex>
        </Flex>

        {children}
      </Flex>
    </Card>
  );
};
